import { useState, useEffect } from 'react';
import { Copy, Check, X } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { WindowsIcon, TuxIcon } from '../components/PlatformIcons.jsx';

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const fmtDateTime = (d) =>
  new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const STATUS = {
  0: { cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', text: 'En attente' },
  1: { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', text: 'Réclamé' },
  2: { cls: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300', text: 'Expiré' },
};
const StatusBadge = ({ s }) => {
  const st = STATUS[s] || STATUS[0];
  return (
    <span className={`px-2 py-0.5 rounded-[10px] text-[10px] font-semibold whitespace-nowrap ${st.cls}`}>
      {st.text}
    </span>
  );
};

const inputCls =
  'bg-sidebar border border-rule rounded-lg px-3.5 py-2.5 text-fg-2 text-[13px] w-full outline-none';
const labelCls = 'block text-[11px] text-muted-2 mb-1.5';
const cardCls = 'bg-card border border-rule rounded-2xl p-5 mb-4';

export function ProvisioningView() {
  const [provisions, setProvisions] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ clientId: '', siteId: '', label: '', count: 1 });
  // En attente par défaut → cache les codes utilisés
  const [filterStatus, setFilterStatus] = useState('0');
  const [filterClient, setFilterClient] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [copiedCmdId, setCopiedCmdId] = useState(null);
  const [copiedLinuxId, setCopiedLinuxId] = useState(null);
  const { sortRows, headerProps, arrow } = useTableSort('createdAt', 'desc');

  const load = async () => {
    try {
      const p = await api.get('/provisions?limit=1000');
      setProvisions(p || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (form.clientId) {
      api.get('/sites?clientId=' + form.clientId).then((s) => setSites(s || []));
    } else {
      setSites([]);
    }
  }, [form.clientId]);

  const handleCreate = async () => {
    if (!form.clientId || !form.siteId) return alert('Sélectionnez un client et un site.');
    const count = Math.max(1, Math.min(100, parseInt(form.count) || 1));
    setCreating(true);
    try {
      if (count === 1) {
        await api.post('/provisions', {
          clientId: form.clientId,
          siteId: form.siteId,
          label: form.label,
        });
      } else {
        await api.post('/provisions/bulk', {
          clientId: form.clientId,
          siteId: form.siteId,
          labelPrefix: form.label,
          count,
        });
      }
      setForm({ clientId: form.clientId, siteId: form.siteId, label: '', count: 1 });
      await load();
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
    setCreating(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce code ?')) return;
    try {
      await api.del('/provisions/' + id);
      await load();
    } catch (e) {
      alert('Erreur: ' + e.message);
    }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Commandes d'installation prêtes à coller, code déjà rempli — rien à éditer.
  // Le Linux passe par /install/linux, qui sert le script avec l'URL de cette
  // plateforme déjà inscrite dedans : d'où l'absence de --url.
  //
  // Côté Windows, on télécharge l'installateur directement plutôt que de passer
  // par /install/windows (« irm | iex ») : ce bootstrap s'est révélé fragile en
  // usage réel (invite de continuation PowerShell sur un copier-coller depuis un
  // client de chat, tiret/apostrophe « intelligent » substitué à l'ASCII).
  // Start-BitsTransfer : passe par WinHTTP plutôt que la pile .NET
  // ServicePointManager, donc pas besoin de forcer TLS 1.2 pour PowerShell 5.1.
  // Un premier essai avait échoué (« impossible d'établir une connexion avec
  // le serveur ») — reproductible avec Invoke-WebRequest aussi, donc lié au
  // réseau du poste (proxy/pare-feu) à ce moment-là, pas à BITS lui-même ;
  // confirmé fonctionnel depuis sur la même machine.
  // PROCESSOR_ARCHITECTURE plutôt que RuntimeInformation.OSArchitecture : cette
  // dernière renvoie $null (plante sur .ToString()) sur certaines installations
  // Windows Server rencontrées en clientèle — .NET Framework incomplet.
  // PROCESSOR_ARCHITEW6432 en priorité pour le cas WOW64 (PowerShell 32 bits
  // sur un Windows 64 bits), où PROCESSOR_ARCHITECTURE seul renvoie x86.
  // L'installateur téléchargé (~140 Mo) est supprimé après coup, qu'il ait
  // réussi ou non : rien à conserver une fois le service en place.
  const silentCommand = (code) =>
    `$ra = if ($env:PROCESSOR_ARCHITEW6432) { $env:PROCESSOR_ARCHITEW6432 } else { $env:PROCESSOR_ARCHITECTURE }; $a = switch ($ra) { 'ARM64' { 'arm64' } default { 'x64' } }; $f = ".\\FonxtioAgent-Installer-$a.exe"; Start-BitsTransfer -Source "${window.location.origin}/updates/FonxtioAgent-Installer-$a.exe" -Destination $f; $p = Start-Process -FilePath $f -ArgumentList '/silent','/code','${code}','/url','${window.location.origin}/api' -Wait -PassThru; Remove-Item -Path $f -Force -ErrorAction SilentlyContinue; "Resultat: $($p.ExitCode)"`;

  const linuxCommand = (code) =>
    `curl -fsSL ${window.location.origin}/install/linux | sudo sh -s -- --code ${code}`;

  const copyCommand = (code, id) => {
    navigator.clipboard.writeText(silentCommand(code));
    setCopiedCmdId(id);
    setTimeout(() => setCopiedCmdId(null), 1500);
  };

  const copyLinuxCommand = (code, id) => {
    navigator.clipboard.writeText(linuxCommand(code));
    setCopiedLinuxId(id);
    setTimeout(() => setCopiedLinuxId(null), 1500);
  };

  const filtered = sortRows(
    provisions.filter(
      (p) =>
        (filterStatus === 'all' || p.status === parseInt(filterStatus)) &&
        (filterClient === 'all' || p.clientId === filterClient)
    ),
    (p, key) => {
      if (key === 'createdAt') return p.createdAt ? new Date(p.createdAt) : null;
      if (key === 'expiresAt') return p.expiresAt ? new Date(p.expiresAt) : null;
      if (key === 'clientSite') return (p.clientName || '') + ' / ' + (p.siteName || '');
      if (key === 'agent') return p.claimedByAgentId ?? null;
      return p[key];
    }
  );
  const counts = {
    all: provisions.length,
    pending: provisions.filter((p) => p.status === 0).length,
    claimed: provisions.filter((p) => p.status === 1).length,
    expired: provisions.filter((p) => p.status === 2).length,
  };

  const filterBtn = (active) =>
    `px-3 py-1 rounded-md border text-xs cursor-pointer transition-colors ${
      active
        ? 'border-blue-500 bg-blue-500/10 text-blue-400 font-semibold hover:bg-blue-500/20'
        : 'border-rule bg-transparent text-muted hover:bg-card-hi hover:text-fg-2 hover:border-rule-2'
    }`;

  return (
    <div>
      <h1 className="text-[22px] font-bold text-fg mb-5">Provisionnement des agents</h1>

      <div className={cardCls}>
        <h3 className="text-[15px] font-semibold text-fg-2 mb-4">Nouveau(x) code(s) de provisionnement</h3>
        <div className="grid grid-cols-[2fr_2fr_2fr_80px_auto] gap-3 items-end">
          <div>
            <label className={labelCls}>Client</label>
            <ClientPicker
              value={form.clientId || null}
              onChange={(id) => setForm({ ...form, clientId: id || '', siteId: '' })}
              placeholder="Sélectionner un client…"
            />
          </div>
          <div>
            <label className={labelCls}>Site</label>
            <select
              className={`${inputCls} cursor-pointer`}
              value={form.siteId}
              onChange={(e) => setForm({ ...form, siteId: e.target.value })}
            >
              <option value="">-- Sélectionner --</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>
              {parseInt(form.count) > 1 ? 'Préfixe libellé' : 'Libellé (optionnel)'}
            </label>
            <input
              className={inputCls}
              placeholder={parseInt(form.count) > 1 ? 'ex: PC → PC-01, PC-02...' : 'ex: PC-Accueil'}
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
          </div>
          <div>
            <label className={labelCls}>Quantité</label>
            <input
              className={inputCls}
              type="number"
              min={1}
              max={100}
              value={form.count}
              onChange={(e) => setForm({ ...form, count: e.target.value })}
            />
          </div>
          <button
            className="px-5 py-2.5 rounded-lg border-none cursor-pointer font-semibold text-[13px] bg-gradient-to-br from-blue-500 to-violet-500 text-white disabled:opacity-60"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? '...' : parseInt(form.count) > 1 ? 'Générer ' + form.count : 'Générer'}
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-3 items-center flex-wrap">
        <span className="text-xs text-muted-2">Statut:</span>
        {[
          ['0', 'En attente', counts.pending],
          ['1', 'Réclamés', counts.claimed],
          ['2', 'Expirés', counts.expired],
          ['all', 'Tous', counts.all],
        ].map(([val, label, n]) => (
          <button key={val} onClick={() => setFilterStatus(val)} className={filterBtn(filterStatus === val)}>
            {label} <span className="text-muted-3 text-[11px]">({n})</span>
          </button>
        ))}
        <span className="text-xs text-muted-2 ml-2">Client:</span>
        <div className="min-w-[200px]">
          <ClientPicker
            value={filterClient === 'all' ? null : filterClient}
            onChange={(id) => setFilterClient(id || 'all')}
            placeholder="Tous les clients"
          />
        </div>
        <span className="text-[11px] text-muted-3 ml-auto">
          {filtered.length +
            ' code' +
            (filtered.length > 1 ? 's' : '') +
            ' affiché' +
            (filtered.length > 1 ? 's' : '')}
        </span>
      </div>

      {loading ? (
        <div className="text-muted-2 p-5">Chargement...</div>
      ) : filtered.length === 0 ? (
        <div className={`${cardCls} text-muted-2 text-center p-10 text-[13px]`}>
          Aucun code de provisionnement.
        </div>
      ) : (
        <div className="bg-card border border-rule rounded-xl overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {[
                  { key: 'code', label: 'Code' },
                  { key: 'status', label: 'Statut' },
                  { key: 'label', label: 'Libellé' },
                  { key: 'clientSite', label: 'Client / Site' },
                  { key: 'createdAt', label: 'Créé' },
                  { key: 'expiresAt', label: 'Expire' },
                  { key: 'agent', label: 'Agent' },
                  { key: null, label: '' },
                ].map((c, i) => (
                  <th
                    key={c.label || 'spacer'}
                    {...headerProps(c.key)}
                    className={`px-2.5 py-2 text-[11px] font-semibold text-muted-2 uppercase tracking-wide border-b border-rule-2 whitespace-nowrap ${
                      i === 7 ? 'text-right' : 'text-left'
                    }`}
                  >
                    {c.label}
                    {arrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="align-middle"
                  style={{
                    borderLeft:
                      p.status === 0
                        ? '3px solid #3b82f6'
                        : p.status === 1
                          ? '3px solid #22c55e'
                          : '3px solid #374151',
                  }}
                >
                  <td className="px-2.5 py-1.5 text-xs border-b border-rule">
                    <div className="flex items-center gap-2">
                      <code
                        className={`font-mono text-[13px] font-bold tracking-wider ${
                          p.status === 0 ? 'text-blue-400' : 'text-muted-2'
                        }`}
                      >
                        {p.code}
                      </code>
                      <button
                        onClick={() => copyCode(p.code, p.id)}
                        title="Copier le code"
                        className={`px-1.5 py-1 rounded border border-rule bg-transparent text-[10px] cursor-pointer inline-flex items-center ${
                          copiedId === p.id ? 'text-ok' : 'text-muted-2'
                        }`}
                      >
                        {copiedId === p.id ? (
                          <Check size={12} strokeWidth={2.5} />
                        ) : (
                          <Copy size={12} strokeWidth={1.75} />
                        )}
                      </button>
                      {p.status === 0 && (
                        <>
                          <button
                            onClick={() => copyCommand(p.code, p.id)}
                            title={
                              'Windows — copier la commande d\'installation (PowerShell administrateur) :\n' +
                              silentCommand(p.code)
                            }
                            className={`px-1.5 py-1 rounded border border-rule bg-transparent text-[10px] cursor-pointer inline-flex items-center gap-1 ${
                              copiedCmdId === p.id ? 'text-ok' : 'text-muted-2'
                            }`}
                          >
                            {copiedCmdId === p.id ? (
                              <Check size={12} strokeWidth={2.5} />
                            ) : (
                              <WindowsIcon size={12} />
                            )}
                          </button>
                          <button
                            onClick={() => copyLinuxCommand(p.code, p.id)}
                            title={'Linux — copier la commande d\'installation (root) :\n' + linuxCommand(p.code)}
                            className={`px-1.5 py-1 rounded border border-rule bg-transparent text-[10px] cursor-pointer inline-flex items-center gap-1 ${
                              copiedLinuxId === p.id ? 'text-ok' : 'text-muted-2'
                            }`}
                          >
                            {copiedLinuxId === p.id ? (
                              <Check size={12} strokeWidth={2.5} />
                            ) : (
                              <TuxIcon size={12} />
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-2.5 py-1.5 text-xs border-b border-rule">
                    <StatusBadge s={p.status} />
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-muted border-b border-rule">{p.label || '—'}</td>
                  <td className="px-2.5 py-1.5 text-xs text-muted border-b border-rule">
                    <div>{p.clientName}</div>
                    <div className="text-[11px] text-muted-3">{p.siteName}</div>
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-muted border-b border-rule whitespace-nowrap">
                    <div>{fmtDateTime(p.createdAt)}</div>
                    <div className="text-[10px] text-muted-3">{p.createdBy}</div>
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-muted border-b border-rule whitespace-nowrap">
                    {p.status === 1 ? '—' : fmtDate(p.expiresAt)}
                  </td>
                  <td className="px-2.5 py-1.5 text-xs text-muted border-b border-rule whitespace-nowrap">
                    {p.claimedByAgentId ? '#' + p.claimedByAgentId : '—'}
                  </td>
                  <td className="px-2.5 py-1.5 text-xs border-b border-rule text-right">
                    {p.status === 0 && (
                      <button
                        onClick={() => handleDelete(p.id)}
                        title="Supprimer"
                        className="px-1.5 py-1 rounded border border-red-950 bg-transparent text-red-300 text-[10px] cursor-pointer hover:bg-red-950/30 inline-flex items-center"
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
